import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { setMoneyContext } from "@/lib/money";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Retry on transient network/5xx errors (e.g. phone waking from sleep) so the
    // user is only logged out on a real 401, not a temporary connectivity blip.
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setMoneyContext(data?.space);
        setUser(data);
        setLoading(false);
        return;
      } catch (err) {
        if (err.response?.status === 401) {
          setUser(false);
          setLoading(false);
          return;
        }
        await new Promise((r) => setTimeout(r, 800));
      }
    }
    setUser(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback handle the exchange first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Relit les droits enregistrés quand l'onglet redevient actif (et toutes les 2 min)
  useEffect(() => {
    const refresh = async () => {
      if (document.visibilityState !== "visible") return;
      try { const { data } = await api.get("/auth/me"); setMoneyContext(data?.space); setUser((u) => (u ? data : u)); } catch {}
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    const t = setInterval(refresh, 120000);
    return () => { document.removeEventListener("visibilitychange", refresh); window.removeEventListener("focus", refresh); clearInterval(t); };
  }, []);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("agent_mode");
    setUser(false);
  };

  const setAgentMode = async (on) => {
    if (on) localStorage.setItem("agent_mode", "1");
    else localStorage.removeItem("agent_mode");
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout, setAgentMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
